/**
 * install-app.tsx — Tải / cài eCDP về máy (PWA): hook dùng chung, hộp hướng dẫn, thẻ nổi mời cài.
 *
 * - `useInstallApp()` — cho nút "Tải ứng dụng" ở menu Cá nhân (máy tính) và hộp Thêm (điện thoại): trình duyệt cài được
 *   một chạm thì mở hộp cài luôn; không thì mở hộp hướng dẫn theo thiết bị. Đã cài → nút tự ẩn.
 * - `InstallGuideDialog` — gắn MỘT lần trong AppLayout (nút nằm trong menu bật ra, menu đóng là mất nếu để hộp trong đó).
 * - `InstallPromoCard` — thẻ nổi ở góc khi vào web; điều phối hiện/ẩn nằm ở entry-prompts.tsx.
 */
import { create } from 'zustand'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Download, MoreVertical, Share, SquarePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { i18n } from '@/lib/i18n'
import { getBranding } from '@/lib/branding'
import { promptInstall, useInstallState, type InstallPlatform } from '@/lib/pwa-install'

const useGuide = create<{ open: boolean; setOpen: (v: boolean) => void }>((set) => ({ open: false, setOpen: (open) => set({ open }) }))

export function useInstallApp() {
  const state = useInstallState()
  const setGuide = useGuide((s) => s.setOpen)
  /** Trả về true nếu đã mở được hộp cài / hộp hướng dẫn. */
  const run = async () => {
    if (state.canPromptNative) {
      const r = await promptInstall()
      if (r === 'accepted') {
        toast.success(i18n.t('sharedMisc:installApp.installingToast'))
        return
      }
      if (r === 'dismissed') return // người dùng đã tự đóng hộp cài — không mở thêm hướng dẫn
    }
    setGuide(true)
  }
  return { ...state, visible: !state.installed, run }
}

function useInstallSteps(): Record<InstallPlatform, { title: string; steps: { icon: typeof Share; text: string }[]; note?: string }> {
  const { t } = useTranslation('sharedMisc')
  return {
    ios: {
      title: t('installApp.ios.title'),
      steps: [
        { icon: Share, text: t('installApp.ios.steps.0') },
        { icon: SquarePlus, text: t('installApp.ios.steps.1') },
        { icon: Download, text: t('installApp.ios.steps.2') },
      ],
      note: t('installApp.ios.note'),
    },
    android: {
      title: t('installApp.android.title'),
      steps: [
        { icon: MoreVertical, text: t('installApp.android.steps.0') },
        { icon: Download, text: t('installApp.android.steps.1') },
        { icon: SquarePlus, text: t('installApp.android.steps.2') },
      ],
    },
    desktop: {
      title: t('installApp.desktop.title'),
      steps: [
        { icon: Download, text: t('installApp.desktop.steps.0') },
        { icon: MoreVertical, text: t('installApp.desktop.steps.1') },
        { icon: SquarePlus, text: t('installApp.desktop.steps.2') },
      ],
      note: t('installApp.desktop.note'),
    },
  }
}

export function InstallGuideDialog() {
  const { t } = useTranslation('sharedMisc')
  const { open, setOpen } = useGuide()
  const { platform } = useInstallState()
  const g = useInstallSteps()[platform]
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{g.title}</DialogTitle>
          <DialogDescription>{t('installApp.guideDescription', { brand: getBranding().brandName })}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3">
          {g.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <s.icon className="h-4 w-4" />
              </span>
              <p className="pt-1 text-sm leading-snug">
                <span className="mr-1 font-semibold text-primary">{i + 1}.</span>
                {s.text}
              </p>
            </li>
          ))}
        </ol>
        {g.note && <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">{g.note}</p>}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Thẻ nổi mời cài. Điện thoại: nằm ngay trên thanh tab dưới, gần hết bề ngang; máy tính: góc dưới-phải.
 * `onClose` = "Để sau" (cũng là nút X); `onInstall` chạy xong thì thẻ tự biến mất vì state đã đổi.
 */
export function InstallPromoCard({ mobile, onClose, onInstall }: { mobile: boolean; onClose: () => void; onInstall: () => void }) {
  const { t } = useTranslation('sharedMisc')
  const name = getBranding().brandName || 'eCDP'
  return (
    <aside
      role="complementary"
      aria-label={t('installApp.promoTitle', { name })}
      className={
        mobile
          ? 'fixed inset-x-3 bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.5rem)] z-40 animate-card-in'
          : 'fixed bottom-5 right-5 z-40 w-[22rem] animate-card-in'
      }
    >
      <div className="relative overflow-hidden rounded-2xl border bg-card shadow-2xl ring-1 ring-primary/10">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-primary/60 to-primary/20" />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('installApp.later')}
          className="absolute right-2 top-3 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 p-4 pt-5">
          <img src="/icons/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl shadow-sm ring-1 ring-border" />
          <div className="min-w-0 flex-1 pr-5">
            <p className="text-sm font-semibold leading-tight">{t('installApp.promoTitle', { name })}</p>
            <p className="mt-1 text-xs leading-snug text-muted-foreground">
              {t('installApp.promoDescription')}
            </p>
          </div>
        </div>
        <div className="flex gap-2 px-4 pb-4">
          <Button className="flex-1" onClick={onInstall}>
            <Download /> {t('installApp.install')}
          </Button>
          <Button variant="ghost" onClick={onClose}>{t('installApp.later')}</Button>
        </div>
      </div>
    </aside>
  )
}
